// A tonal initials avatar (prototype .avatar; design brief §3: radius-full only for avatars/status dots/
// toggles). Shape and the three prototype sizes (22 / 30 / 38px, in rem) come from the ported handoff
// stylesheet; the tone is derived deterministically from the initials so a given person keeps a stable
// colour — set as inline `var(--token)` values because every tone is a GOVERNED token (no raw hex; the
// prototype's inline AV_TONES hexes are replaced by their nearest governed tokens, G-181).

/** The token tone palette (background + text) — each entry is a pair of governed custom properties. */
const TONES: ReadonlyArray<{ bg: string; text: string }> = [
  { bg: 'var(--lichen)', text: 'var(--forest-deep)' },
  { bg: 'var(--sage-tint-2)', text: 'var(--moss)' },
  { bg: 'var(--surface-sunken)', text: 'var(--slate)' },
  { bg: 'var(--lichen-tint)', text: 'var(--forest)' },
];

/** Pick a stable tone index from the initials (sum of char codes), so one person keeps one colour. */
function toneFor(initials: string): { bg: string; text: string } {
  const sum = initials.split('').reduce((total, char) => total + char.charCodeAt(0), 0);
  // `TONES` is non-empty and the index is a bounded modulo, so a tone always exists; the fallback satisfies
  // the strict no-unchecked-index rule without ever being reached.
  return TONES[sum % TONES.length] ?? TONES[0]!;
}

/** The prototype's avatar size classes: sm 22px, default 30px, lg 38px (handoff .avatar/.sm/.lg). */
const SIZE_CLASS: Readonly<Record<'sm' | 'md' | 'lg', string>> = {
  sm: 'avatar sm',
  md: 'avatar',
  lg: 'avatar lg',
};

export interface AvatarProps {
  readonly initials: string;
  readonly size?: 'sm' | 'md' | 'lg';
}

/** A circular tonal avatar showing the person's initials in a governed tone. */
export function Avatar({ initials, size = 'md' }: AvatarProps): React.JSX.Element {
  const tone = toneFor(initials);
  return (
    <span
      aria-hidden="true"
      className={SIZE_CLASS[size]}
      style={{ background: tone.bg, color: tone.text }}
    >
      {initials}
    </span>
  );
}
